import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Clapperboard,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Loader2,
  ExternalLink,
  Clock,
  Copy,
  Check,
} from "lucide-react";
import PreDemoChecklist from "@/components/PreDemoChecklist";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEMO_EMAIL = "student-complete@test.com";
const DEMO_PASSWORD = "student123";

function authHeaders() {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

/**
 * Inline pill button that copies a string to the clipboard and shows
 * 2-second "Copied ✓" feedback. Falls back to a hidden textarea for
 * older browsers / non-secure contexts.
 */
function CopyChip({ text, label, testId, variant = "light" }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async (e) => {
    e.stopPropagation();
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  }, [text]);

  const variants = {
    light:
      "bg-white text-emerald-700 border border-emerald-300 hover:bg-emerald-50",
    dark:
      "bg-white/15 text-white border border-white/30 hover:bg-white/25",
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${variants[variant]}`}
      title={`Copy ${label.toLowerCase()}`}
    >
      {copied ? (
        <>
          <Check className="w-3.5 h-3.5" />
          Copied
        </>
      ) : (
        <>
          <Copy className="w-3.5 h-3.5" />
          {label}
        </>
      )}
    </button>
  );
}

function formatTimestamp(iso) {
  if (!iso) return "Never";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
}

function StatPill({ label, value, ok }) {
  const color = ok
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-amber-50 text-amber-700 border-amber-200";
  return (
    <div
      className={`rounded-lg border px-3 py-2 ${color}`}
      data-testid={`demo-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-lg font-bold leading-tight">{value}</div>
    </div>
  );
}

function DemoModePanel() {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [checklistReady, setChecklistReady] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/demo-mode/status`, authHeaders());
      setStatus(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load demo status");
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Keyboard shortcut: Alt/Option + R opens the confirmation modal.
  // We deliberately avoid Ctrl/Cmd+Shift+R (browser hard-reload) and we ignore
  // the keypress when the user is typing in an input or another modal is open.
  useEffect(() => {
    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      return (
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        el.isContentEditable
      );
    };

    const handler = (e) => {
      if (
        e.altKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.shiftKey &&
        (e.key === "r" || e.key === "R" || e.code === "KeyR") &&
        !isTypingTarget(e.target) &&
        !showConfirm &&
        !resetting
      ) {
        e.preventDefault();
        setShowConfirm(true);
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [showConfirm, resetting]);

  const handleConfirmReset = async () => {
    setShowConfirm(false);
    setResetting(true);
    setError(null);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/demo-mode/reset`,
        {},
        authHeaders()
      );
      setResult(res.data);
      // Refresh status pill cards
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || "Demo reset failed");
    } finally {
      setResetting(false);
    }
  };

  const demo = status?.demo_user;
  const last = status?.last_activation;
  const isHealthy =
    demo &&
    demo.lessons_completed >= 42 &&
    demo.session_assessments >= 7 &&
    demo.has_ai_plan;

  return (
    <div className="space-y-6" data-testid="demo-mode-panel">
      {/* Pre-Demo Checklist (above the hero so presenters run through it first) */}
      <PreDemoChecklist onAllReady={setChecklistReady} />

      {/* Hero */}
      <div className="bg-gradient-to-br from-fuchsia-600 via-purple-600 to-indigo-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Clapperboard className="w-6 h-6" />
              <h2 className="text-2xl font-bold">Demo Mode</h2>
            </div>
            <p className="text-white/85 max-w-xl text-sm">
              One-click reset for the showcase student account
              (<span className="font-mono">student-complete@test.com</span>).
              Re-seeds 42 lessons, 7 session reflections, the Mini Business
              Plan, regenerates a fresh AI Business Plan with GPT-4o, and
              pins the regeneration counter to 1/3 so you always have 2 live
              regenerations during demos.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <CopyChip
                text={DEMO_EMAIL}
                label="Copy email"
                testId="hero-copy-email-btn"
                variant="dark"
              />
              <CopyChip
                text={DEMO_PASSWORD}
                label="Copy password"
                testId="hero-copy-password-btn"
                variant="dark"
              />
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <button
              onClick={() => setShowConfirm(true)}
              disabled={resetting}
              data-testid="open-demo-reset-modal-btn"
              className={`inline-flex items-center gap-2 bg-white text-purple-700 hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold px-5 py-3 rounded-xl shadow-md transition-all ${
                checklistReady && !resetting
                  ? "ring-4 ring-emerald-300/70 shadow-emerald-300/50"
                  : ""
              }`}
            >
              {resetting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Resetting…
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  Reset & Prepare Demo
                </>
              )}
            </button>
            <span
              className="text-[11px] text-white/70 inline-flex items-center gap-1"
              data-testid="demo-reset-shortcut-hint"
              title="Keyboard shortcut: Alt + R (Option + R on Mac)"
            >
              Shortcut:
              <kbd className="bg-white/15 border border-white/30 rounded px-1.5 py-0.5 font-mono text-[10px] tracking-wide">
                Alt
              </kbd>
              <span className="opacity-70">+</span>
              <kbd className="bg-white/15 border border-white/30 rounded px-1.5 py-0.5 font-mono text-[10px] tracking-wide">
                R
              </kbd>
            </span>
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4"
          data-testid="demo-error"
        >
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-red-800">Something went wrong</div>
            <div className="text-sm text-red-700">{error}</div>
          </div>
        </div>
      )}

      {/* Success banner */}
      {result && (
        <div
          className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4"
          data-testid="demo-success"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-emerald-800">
              Demo ready ✅ — completed in {result.elapsed_seconds}s
            </div>
            <div className="text-sm text-emerald-700 mt-1">
              {result.demo_user?.name} ·{" "}
              {result.demo_user?.lessons_completed}/{result.demo_user?.total_lessons} lessons ·{" "}
              {result.demo_user?.session_assessments}/7 reflections ·
              AI plan v{result.demo_user?.ai_plan_version} ({result.demo_user?.remaining_regenerations} regenerations remaining)
            </div>

            {/* Quick-copy credentials + open-login row */}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <CopyChip
                text={DEMO_EMAIL}
                label="Email"
                testId="copy-demo-email-btn"
              />
              <CopyChip
                text={DEMO_PASSWORD}
                label="Password"
                testId="copy-demo-password-btn"
              />
              <CopyChip
                text={`Email: ${DEMO_EMAIL}\nPassword: ${DEMO_PASSWORD}`}
                label="Both"
                testId="copy-demo-both-btn"
              />
              <a
                href="/login"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                data-testid="demo-go-to-login-link"
              >
                Open login page <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Status grid */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-500" />
              Current Demo State
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {isHealthy ? (
                <span className="text-emerald-700 font-medium">
                  ✓ Ready for live presentation
                </span>
              ) : (
                <span className="text-amber-700 font-medium">
                  ⚠ Account is missing data — run reset before demo
                </span>
              )}
            </p>
          </div>
          <button
            onClick={fetchStatus}
            disabled={loadingStatus}
            data-testid="refresh-demo-status-btn"
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className={`w-4 h-4 ${loadingStatus ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {loadingStatus && !demo ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : demo ? (
          <>
            <div className="text-sm text-gray-700 mb-3">
              <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
                {demo.email}
              </span>{" "}
              · <span className="font-medium">{demo.name}</span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatPill
                label="Lessons"
                value={`${demo.lessons_completed}/42`}
                ok={demo.lessons_completed >= 42}
              />
              <StatPill
                label="Reflections"
                value={`${demo.session_assessments}/7`}
                ok={demo.session_assessments >= 7}
              />
              <StatPill
                label="Capstone Fields"
                value={`${demo.capstone_fields_count}/7`}
                ok={demo.capstone_fields_count >= 7}
              />
              <StatPill
                label="AI Plan"
                value={
                  demo.has_ai_plan
                    ? `v${demo.ai_plan_version} · ${demo.ai_plan_remaining_regenerations} left`
                    : "Not generated"
                }
                ok={!!demo.has_ai_plan}
              />
              <StatPill label="Points" value={demo.points} ok={demo.points > 0} />
              <StatPill
                label="Badges"
                value={demo.badges_count}
                ok={demo.badges_count > 0}
              />
              <StatPill
                label="AI Plan Sections"
                value={demo.ai_plan_sections}
                ok={demo.ai_plan_sections > 0}
              />
            </div>
          </>
        ) : (
          <div className="text-gray-500 text-sm">No demo account found.</div>
        )}
      </div>

      {/* Last activation */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-gray-500" />
          Last Activation
        </h3>
        {last ? (
          <div
            className="text-sm text-gray-700 grid sm:grid-cols-2 gap-2"
            data-testid="last-activation"
          >
            <div>
              <span className="text-gray-500">When:</span>{" "}
              <span className="font-medium">{formatTimestamp(last.created_at)}</span>
            </div>
            <div>
              <span className="text-gray-500">Triggered by:</span>{" "}
              <span className="font-medium">
                {last.triggered_by_name || last.triggered_by}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Took:</span>{" "}
              <span className="font-medium">{last.elapsed_seconds}s</span>
            </div>
            <div>
              <span className="text-gray-500">AI plan version:</span>{" "}
              <span className="font-medium">v{last.ai_plan_version}</span>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-500">
            No demo resets recorded yet — click <strong>Reset & Prepare Demo</strong> above to
            create one.
          </div>
        )}
      </div>

      {/* Confirmation modal */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="demo-confirm-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Clapperboard className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-gray-900">
                  Reset & prepare demo account?
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  This will overwrite{" "}
                  <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                    student-complete@test.com
                  </span>{" "}
                  progress, regenerate the AI Business Plan via GPT-4o, and
                  reset the regeneration counter to 1/3.
                </p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 mb-5">
              <strong>Heads up:</strong> Takes ~10–15 seconds. One LLM call
              will be billed against your Universal Key.
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowConfirm(false)}
                data-testid="demo-cancel-btn"
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReset}
                data-testid="demo-confirm-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-purple-600 text-white hover:bg-purple-700"
              >
                <RefreshCw className="w-4 h-4" />
                Yes, reset demo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay during reset */}
      {resetting && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          data-testid="demo-loading-overlay"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-purple-600 animate-pulse" />
            </div>
            <h3 className="font-bold text-lg text-gray-900 mb-1">
              Preparing demo account…
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Re-seeding progress · Regenerating AI plan via GPT-4o
            </p>
            <Loader2 className="w-6 h-6 animate-spin text-purple-500 mx-auto" />
          </div>
        </div>
      )}
    </div>
  );
}

export default DemoModePanel;
